import { AfterViewInit, Directive, EventEmitter, Host, Input, OnDestroy, Output, Self, ViewChild } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

/**
 * Apply to an `input` associated with a `mat-autocomplete` field so that when a user has an option highlighted and presses tab, that option is selected.
 *
 * **IMPORTANT: Tab selection does not emit to the normal `optionSelected` property of the `mat-autocomplete`**. Writing a new value to the autocomplete's
 * associated `input` only updates the UI, it does not cause `optionSelected` to emit. Instead, this directive adds another emitter to the `input` element
 * that is specifically for tab selected values. **You are responsible for subscribing to that `optionTabSelected` emitter and combining it with the rest
 * of the autocomplete selected output. Also note that the value of the selection is emitted, not the selection itself.**
 *
 * @see https://github.com/angular/components/issues/3334#issuecomment-469718514
 * @example <mat-form-field>
 *  <input ... [matAutocomplete]="auto" ofiAutoSelectOnTab (optionTabSelected)="onSelect($event)>
 *   <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onSelect($event.option.value)><mat-option>...</mat-option></mat-autocomplete>
 * </mat-form-field>
 */
// tslint:disable-next-line:directive-selector
@UntilDestroy()
@Directive({
    selector: '[ofiAutoSelectOnTab]',
    standalone: false
})
export class FoundationAutoSelectOnTabDirective implements AfterViewInit, OnDestroy {

    @Input() matAutocomplete: MatAutocomplete;
    /** Emits the full practice object on selection. */
    @Output() optionTabSelected = new EventEmitter();

    // @ViewChild(MatAutocompleteTrigger) _auto: MatAutocompleteTrigger;

    /**
   * Creates an instance of FoundationAutoSelectOnTabDirective.
   * @param MatAutocompleteTrigger the autoComplete element attached to this directive?
   * @param NgControl ngControl The input element that this directive is on.
   */
    constructor (
        @Host() @Self() private readonly autoCompleteTrigger: MatAutocompleteTrigger,
        private readonly ngControl: NgControl
    ) { }

    ngAfterViewInit (): void {
        this.autoCompleteTrigger.panelClosingActions
            .pipe(untilDestroyed(this))
            .subscribe(x => {
                if (this.autoCompleteTrigger.activeOption) {
                    const value = this.autoCompleteTrigger.activeOption.value;
                    if (this.ngControl) {
                        this.ngControl.control.setValue(value, { emit: false });
                        this.optionTabSelected.emit(value);
                    }
                    this.autoCompleteTrigger.writeValue(value);
                }
            });
    }

    ngOnDestroy (): void { }
}
